import {useEffect, useState, useCallback} from 'react';
import {useSearchParams} from 'react-router-dom';
import api from '../api/axios';

/**
 * Generate a cryptographically random PKCE code_verifier (43-128 chars, URL-safe).
 */
function generateCodeVerifier() {
    const array = new Uint8Array(48); // 48 bytes => 64-char base64url
    window.crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
}

/**
 * Derive the S256 code_challenge from a code_verifier.
 * Returns a Promise<string>.
 */
async function generateCodeChallenge(verifier) {
    const encoded = new TextEncoder().encode(verifier);
    const digest = await window.crypto.subtle.digest('SHA-256', encoded);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
}

export function usePopupFlow() {
    const [searchParams] = useSearchParams();
    // Read from URL, or fallback to sessionStorage if returning from a Social Login redirect
    const urlClientId = searchParams.get('client_id');
    const urlRedirectUri = searchParams.get('redirect_uri');
    const urlPrompt = searchParams.get('prompt');
    const urlState = searchParams.get('state');

    const [oauthParams, setOauthParams] = useState(() => {
        // If URL explicitly provides parameters, this is a fresh external flow — save them.
        if (urlClientId && urlRedirectUri) {
            const params = {
                clientId: urlClientId,
                redirectUri: urlRedirectUri,
                prompt: urlPrompt,
                state: urlState
            };
            sessionStorage.setItem('oauth_flow_params', JSON.stringify(params));
            return params;
        }

        // If URL has no parameters, but sessionStorage exists AND we have a window.opener,
        // confidently restore the parameters (avoids strict brittle pathname checks).
        const stored = sessionStorage.getItem('oauth_flow_params');
        if (stored && typeof window !== 'undefined' && window.opener) {
            return JSON.parse(stored);
        }

        // Otherwise, this is a fresh internal flow. Clean up any stale state.
        sessionStorage.removeItem('oauth_flow_params');
        sessionStorage.removeItem('oauth_pkce_verifier');
        return null;
    });

    const clientId = oauthParams?.clientId;
    const redirectUri = oauthParams?.redirectUri;
    const prompt = oauthParams?.prompt;
    const state = oauthParams?.state;

    const isExternal = !!(clientId && redirectUri);

    const [isValidating, setIsValidating] = useState(isExternal);
    const [validationError, setValidationError] = useState(null);

    // Synchronously restore verifiedOrigin if it was saved in sessionStorage
    const [verifiedOrigin, setVerifiedOrigin] = useState(() => {
        return oauthParams?.verifiedOrigin || null;
    });

    // PKCE pair — generated once per popup session, persisted in sessionStorage
    // so it survives prompt=login re-renders within the same popup window.
    const [pkce] = useState(() => {
        if (!isExternal) return null;

        // Reuse a pair already generated for this popup session (avoids mismatch
        // if the component re-mounts during an intra-popup navigation).
        const stored = sessionStorage.getItem('oauth_pkce_verifier');
        if (stored) {
            return {verifier: stored, challengeReady: false};
        }

        const verifier = generateCodeVerifier();
        sessionStorage.setItem('oauth_pkce_verifier', verifier);
        return {verifier, challengeReady: false};
    });

    const [codeChallenge, setCodeChallenge] = useState(null);

    // Derive the challenge asynchronously once on mount
    useEffect(() => {
        if (!isExternal || !pkce) return;
        generateCodeChallenge(pkce.verifier).then(setCodeChallenge);
    }, [isExternal, pkce]);

    useEffect(() => {
        if (!isExternal) {
            setIsValidating(false);
            return;
        }

        // If we already have the verifiedOrigin from sessionStorage, skip the API call.
        if (verifiedOrigin) {
            setIsValidating(false);
            return;
        }

        const validateClient = async () => {
            try {
                const res = await api.get('/oauth/validate-client', {
                    params: {client_id: clientId, redirect_uri: redirectUri}
                });

                if (res.data && res.data.valid) {
                    setVerifiedOrigin(res.data.origin);

                    // Save verifiedOrigin to sessionStorage to survive Social Login redirects
                    const stored = sessionStorage.getItem('oauth_flow_params');
                    if (stored) {
                        const parsed = JSON.parse(stored);
                        parsed.verifiedOrigin = res.data.origin;
                        sessionStorage.setItem('oauth_flow_params', JSON.stringify(parsed));
                    }
                } else {
                    setValidationError('Invalid client or redirect URI configuration.');
                }
            } catch (err) {
                setValidationError(
                    err.response?.data?.message || err.message || 'Failed to validate third-party client.'
                );
            } finally {
                setIsValidating(false);
            }
        };

        validateClient();
    }, [isExternal, clientId, redirectUri, verifiedOrigin]);

    const handleLoginSuccess = useCallback(async (token) => {
        if (!isExternal) {
            // Internal flow — notify opener and close
            if (typeof window !== 'undefined' && window.opener) {
                window.opener.postMessage(
                    {success: true, type: 'LOGIN_SUCCESS', status: 'success'},
                    window.location.origin
                );
                window.close();
            }
            return;
        }

        // External flow — request authorization code from backend (with PKCE)
        const activeToken = token || localStorage.getItem('access_token');

        if (!verifiedOrigin) {
            console.error('usePopupFlow: verifiedOrigin not set — cannot send postMessage safely');
            return;
        }

        const finalChallenge = codeChallenge || (pkce?.verifier ? await generateCodeChallenge(pkce.verifier) : null);

        if (!finalChallenge) {
            console.error('usePopupFlow: PKCE code_challenge not ready and could not be generated');
            return;
        }

        try {
            const res = await api.post('/oauth/authorize', {
                client_id: clientId,
                redirect_uri: redirectUri,
                state: state || undefined,
                code_challenge: finalChallenge,
                code_challenge_method: 'S256',
            }, {
                headers: {Authorization: `Bearer ${activeToken}`}
            });

            if (res.data && res.data.code) {
                console.log(res);
                // Clean up session storage parameters
                sessionStorage.removeItem('oauth_pkce_verifier');
                sessionStorage.removeItem('oauth_flow_params');

                if (typeof window !== 'undefined' && window.opener) {
                    window.opener.postMessage(
                        {
                            success: true,
                            type: 'OAUTH_CODE',
                            code: res.data.code,
                            state: state || undefined,
                            // Pass the verifier to the opener so it can exchange the code.
                            // This is safe because the opener IS the external app that initiated
                            // the flow — the verifier has no value without the code.
                            code_verifier: pkce.verifier,
                        },
                        verifiedOrigin
                    );
                    window.close();
                }
            }
        } catch (err) {
            console.error('usePopupFlow: Failed to generate authorization code:', err);
            if (typeof window !== 'undefined' && window.opener) {
                window.opener.postMessage(
                    {
                        success: false,
                        type: 'OAUTH_ERROR',
                        error: err.response?.data?.message || 'Failed to generate authorization code',
                        state: state || undefined,
                    },
                    verifiedOrigin
                );
                window.close();
            }
        }
    }, [isExternal, clientId, redirectUri, state, verifiedOrigin, codeChallenge, pkce]);

    return {
        isExternal,
        clientId,
        redirectUri,
        prompt,
        isValidating,
        validationError,
        handleLoginSuccess,
        verifiedOrigin,
    };
}
